import { createSlice } from "@reduxjs/toolkit";
import toast from "react-hot-toast";
import { updateComplexItem, updateItem } from "../reducerUtils";
import {
  addHistoryComplexReducer,
  addHistoryReducer,
  consumeHistoryChooseGripsReducer,
  consumeHistoryEmgSettingsReducer,
  consumeHistoryGripsConfigurationReducer,
  consumeHistoryProsthesisSettingsReducer,
  consumeHistoryReducer,
  importConfigReducer,
  resetGripConfigurationReducer,
  resetGripsConfigurationReducer,
  setAutograspReducer,
  setCoContractionTimingsReducer,
  setControlConfigReducer,
  setControlModesReducer,
  setEmgGainsReducer,
  setEmgSpikeReducer,
  setEmgThresholdsReducer,
  setFingerStrengthReducer,
  setGripsPairsReducer,
  setGripsPositionsReducer,
  setGripsSequentialReducer,
  setGripSwitchingModesReducer,
  setHoldOpenReducer,
  setInputSitesReducer,
  setIntervalReducer,
  setPulseTimingsReducer,
  setSoftGripReducer,
  setSpeedControlStrategyReducer,
  setUnsavedReducer,
  toggleComplexItemReducer,
} from "./bluetoothHelpers/reducers";
import { initialState } from "./bluetoothHelpers/initialState";
import {
  disconnectDevice,
  getInitialConfig,
  handleProcedure,
  sendAllFingers,
  sendFreeze,
  sendWholeConfig,
  resetToDefault,
  getFirmwareDevice,
  connectDevice,
  getPcbVersionDevice,
} from "./bluetoothHelpers/asyncThunks";
import {
  disconnectDeviceFulfilled,
  getInitialConfigFulfilled,
  handleProcedureFulfilled,
  sendAllFingersConfigFulfilled,
  sendFreezeFulfilled,
  sendWholeConfigFulfilled,
  resetToDefaultFulfilled,
  getFirmwareDeviceFulfilled,
  connectDeviceFulfilled,
  getPcbVersionDeviceFulfilled,
} from "./bluetoothHelpers/extraReducers";

export const bluetoothSlice = createSlice({
  name: "bluetooth",
  initialState,
  reducers: {
    setItem: updateItem,
    setComplexItem: updateComplexItem,
    toggleComplexItem: toggleComplexItemReducer,
    addHistory: addHistoryReducer,
    addHistoryComplex: addHistoryComplexReducer,
    consumeHistory: consumeHistoryReducer,
    consumeHistoryChooseGrips: consumeHistoryChooseGripsReducer,
    consumeHistoryProsthesisSettings: consumeHistoryProsthesisSettingsReducer,
    consumeHistoryGripsConfiguration: consumeHistoryGripsConfigurationReducer,
    consumeHistoryEmgSettings: consumeHistoryEmgSettingsReducer,
    setControlConfig: setControlConfigReducer,
    importConfig: importConfigReducer,
    setUnsaved: setUnsavedReducer,
    resetGripsConfiguration: resetGripsConfigurationReducer,
    resetGripConfiguration: resetGripConfigurationReducer,
    setEmgThresholds: setEmgThresholdsReducer,
    setEmgGains: setEmgGainsReducer,
    setGripsPositions: setGripsPositionsReducer,
    setGripsPairs: setGripsPairsReducer,
    setGripSequential: setGripsSequentialReducer,
    setInputSites: setInputSitesReducer,
    setControlModes: setControlModesReducer,
    setSpeedControlStrategy: setSpeedControlStrategyReducer,
    setGripSwitchingModes: setGripSwitchingModesReducer,
    setInterval: setIntervalReducer,
    setSoftGrip: setSoftGripReducer,
    setFingerStrength: setFingerStrengthReducer,
    setEmgSpike: setEmgSpikeReducer,
    setAutograsp: setAutograspReducer,
    setHoldOpen: setHoldOpenReducer,
    setPulseTimings: setPulseTimingsReducer,
    setCoContractionTimings: setCoContractionTimingsReducer,
  },
  extraReducers: (builder) => {
    builder.addCase(resetToDefault.fulfilled, resetToDefaultFulfilled);
    builder.addCase(resetToDefault.rejected, () => {
      toast.remove();
      toast.error("Couldn't restore default config");
    });
    builder.addCase(disconnectDevice.fulfilled, disconnectDeviceFulfilled);
    builder.addCase(disconnectDevice.rejected, () => {
      toast.remove();
      toast.error("Couldn't disconnect the device");
    });
    builder.addCase(connectDevice.fulfilled, connectDeviceFulfilled);
    builder.addCase(connectDevice.rejected, () => {
      toast.remove();
      toast.error("Could not connect to the prosthesis");
    });
    builder.addCase(getFirmwareDevice.fulfilled, getFirmwareDeviceFulfilled);
    builder.addCase(getFirmwareDevice.rejected, () => {
      toast.remove();
      toast.error("Prosthesis firmware unknown");
    });
    builder.addCase(getInitialConfig.fulfilled, getInitialConfigFulfilled);
    builder.addCase(getInitialConfig.rejected, () => {
      toast.remove();
      toast.error("Could not connect to the prosthesis");
    });
    builder.addCase(sendFreeze.fulfilled, sendFreezeFulfilled);
    builder.addCase(sendFreeze.rejected, (state, action: any) => {
      toast.remove();
      toast.error(action.payload);
    });
    builder.addCase(handleProcedure.fulfilled, handleProcedureFulfilled);
    builder.addCase(handleProcedure.rejected, (state, action: any) => {
      toast.remove();
      toast.error(action.payload);
    });
    builder.addCase(sendWholeConfig.fulfilled, sendWholeConfigFulfilled);
    builder.addCase(sendWholeConfig.rejected, () => {
      toast.remove();
      toast.error("Something went wrong when sending config");
    });
    builder.addCase(sendAllFingers.fulfilled, sendAllFingersConfigFulfilled);
    builder.addCase(sendAllFingers.rejected, () => {
      toast.remove();
      toast.error("Something went wrong when sending config");
    });
    builder.addCase(
      getPcbVersionDevice.fulfilled,
      getPcbVersionDeviceFulfilled
    );
    builder.addCase(getPcbVersionDevice.rejected, () => {
      toast.error("Couldn't check pcb version");
    });
  },
});

export const {
  addHistory,
  addHistoryComplex,
  consumeHistory,
  consumeHistoryProsthesisSettings,
  setItem,
  setComplexItem,
  setControlConfig,
  importConfig,
  toggleComplexItem,
  setUnsaved,
  resetGripsConfiguration,
  resetGripConfiguration,
  setInputSites,
  setSpeedControlStrategy,
  setGripSwitchingModes,
  setInterval,
  setSoftGrip,
  setFingerStrength,
  setEmgSpike,
  setAutograsp,
  setHoldOpen,
  setControlModes,
  setPulseTimings,
  setCoContractionTimings,
  consumeHistoryGripsConfiguration,
  setGripsPairs,
  setGripSequential,
  consumeHistoryChooseGrips,
  consumeHistoryEmgSettings,
  setEmgThresholds,
  setEmgGains,
  setGripsPositions,
} = bluetoothSlice.actions;

export default bluetoothSlice.reducer;
